import moment from 'moment'
import 'moment-timezone'
import { debugLog } from './debugLog'
import { parseModelCode } from './lib/modelCodeParser'
import { parseFirmwareString } from './lib/firmwareStringParser'
import { authenticator } from './authenticator'
import { getEnglishText } from './dictionary'
import config from './config'
import { clearChartDataCache } from './lib/chartDataCache'
import { DARK_MODE_STORAGE_KEY } from './lib/darkMode'
import { APP_VERSION_STORAGE_KEY } from './lib/appVersion'
import { clearStaffViewAs } from './lib/staffViewAs'
import { queryString } from './lib/queryString'
import {
  isLegacyP3Unit,
  customerOwnsOnlyLegacyUnits,
  customerHasModernUnit,
  getUnitTimezone,
  getLegacyP3LastChartDayStart,
  getLegacyP3DataCutoffEnd,
  selectedUnitsIncludeLegacyP3,
  selectedUnitsHaveChartCsvExport,
  selectedUnitsHaveChartCsvFullExport,
  getChartCsvExportEarliestDay,
  CHART_CSV_FULL_EXPORT_EARLIEST_DATE,
  CHART_CSV_EXPORT_HISTORY_MAX_DAYS,
  allSelectedUnitsAreLegacyP3,
  getMaxChartDayForUnits,
  clampChartDayToLegacyLimit,
  isLegacyUnitLiveViewResponse,
  shouldApplyLegacyP3Restrictions,
  countReportingP3Units,
  formatSelectedUnitsCountSuffix
} from './lib/legacyP3Unit'

async function cleanup({ clearCache = false, signIn = false, signOut = false, redirect = null, reload = false, initiator = 'UNKNOWN `initiator`' }) {
  debugMessage(`${initiator} called cleanup()`)

  if (clearCache) {
    debugMessage('Clearing cache ...', 'cleanup()')

    /////////////////////////////////////////////////////////////
    // copy any `action` item for preservation after cache clear!
    const protectedItem = window.localStorage.getItem('action')
    if (protectedItem) {
      debugLog('Action', 'Protected `action` found', protectedItem)
    }
    const protectedDarkMode = window.localStorage.getItem(DARK_MODE_STORAGE_KEY)
    const protectedAppVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY)
    /////////////////////////////////////////////////////////////

    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach(async (name) => {
          debugLog('name', name)
          await caches.delete(name)
        })
      })
    }
    await clearChartDataCache()
    window.localStorage.clear()

    ////////////////////////////////////////////////////
    // recover protected `action` item
    if (protectedItem) {
      window.localStorage.setItem('action', protectedItem)
      debugLog('Action', 'Recovered protected `action` item in localstorage')
      debugLog('Action', 'protectedItem', protectedItem)
    }
    if (protectedDarkMode) {
      window.localStorage.setItem(DARK_MODE_STORAGE_KEY, protectedDarkMode)
    }
    if (protectedAppVersion) {
      window.localStorage.setItem(APP_VERSION_STORAGE_KEY, protectedAppVersion)
    }
    ////////////////////////////////////////////////////
  }

  if (signOut) {
    debugMessage('authenticator.signOut()', 'cleanup()')
    clearStaffViewAs()
    await authenticator.signOut()
    // debugMessage('Redirecting to /', 'cleanup()')
    // window.location = '/'
  }

  if (signIn) {
    debugMessage('Redirecting to /login', 'cleanup()')
    await authenticator.signIn()
    // window.location = '/login'
  }

  if (redirect) {
    debugMessage(`Redirecting to ${redirect} ...`, 'cleanup()')
    window.location = redirect
  }

  if (reload) {
    debugMessage('window.location.reload()', 'cleanup()')
    window.location.reload(true)
  }
}

function getPaymentGatewayUrl() {
  switch (process.env.REACT_APP_PAYMENT_GATEWAY_ENV) {
    case 'dev':
    case 'local':
      return 'payments-dev.powervault.co.uk'
    case 'test':
      return 'payments-test.powervault.co.uk'
    case 'staging':
    case 'prod':
    default:
      return 'payments.powervault.co.uk'
  }
}

function launchAccountPayment({ customerAccountId, productId }) {
  window.location.href = `https://${getPaymentGatewayUrl()}/?productId=${productId}&accountId=${customerAccountId}&hash=_`
}

function launchUnitPayment({ customerAccountId, configId, productId }) {
  window.location.href = `https://${getPaymentGatewayUrl()}/?productId=${productId}&accountId=${customerAccountId}&configId=${configId}&hash=_`
}

const debugMessage = (message, component = '') => {
  if (component) debugLog(`DEBUG - ${component} ::`, message)
  else debugLog(`DEBUG - ${message}`)
}

const friendlyFormat = (key, value) => {
  // returns [value, unit]

  function formatMicroPowerValue(value, suffix = '') {
    let buffer = value / 1000000 // convert to kW
    let unit = 'kW'

    function addCommas(value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }

    if (buffer > 1000) {
      buffer = buffer / 1000
      unit = 'MW'
    }

    buffer = Number(buffer.toPrecision(3)) // to 3 significant figures
    if (buffer < 100) buffer = buffer.toFixed(1) // to 1 decimal place if below 100
    else buffer = buffer.toFixed(0) // remove decimals if over 100
    buffer = addCommas(buffer) // add commas

    return [buffer, `${unit}${suffix}`]
  }

  if (value === null || value === undefined || isNaN(value)) return [value, null]

  switch (key) {
    case 'batteryType':
      switch (value) {
        case 'eco':
          return ['Powervault 3 ECO', null]
        case 'amita':
          return ['Powervault 3', null]
        default:
          return [value, null]
      }
    case 'batteryCapacity':
      return [value, 'kWh']

    case 'instant_grid':
    case 'instant_battery':
      return formatMicroPowerValue(value)
    case 'instant_solar':
    case 'instant_demand':
      return formatMicroPowerValue(Math.abs(value))

    case 'instant_soc':
      return [parseInt(value, 10), '%']

    case 'chart_solar_consumed_by_home':
    case 'chart_battery_output_consumed_by_home':
    case 'chart_grid_consumed_by_home':
    case 'chart_battery_input_from_solar':
    case 'chart_solar_exported':
    case 'chart_accessory_clamp':
    case 'generic_mW':
      return [roundToFixed(value / 1000000, 1), 'kW']

    case 'chart_battery_input_from_grid':
      if (value < 100000) return [0, 'kW']
      else return [roundToFixed(value / 1000000, 1), 'kW']

    case 'daily_total_solar_generated':
    case 'daily_total_solar_consumed':
    case 'daily_total_grid_import':
    case 'daily_total_grid_export':
    case 'daily_total_home_consumed':
    case 'daily_total_battery_discharged':
      return formatMicroPowerValue(value, 'h')

    case 'daily_total_aux_consumed':
      return formatMicroPowerValue(value, 'h')

    case 'installationDateUtc':
      return [moment(value, 'x').format('LLL'), null]

    case 'warrantyActivationDate':
      return [moment(value).format('YYYY-MM-DD'), null]

    default:
      return [value, null]
  }
}

const isJson = (str) => {
  try {
    JSON.parse(str)
  } catch (e) {
    return false
  }
  return true
}

const roundTo = (value, places) => {
  return Math.round(value * Math.pow(10, places)) / Math.pow(10, places)
}

const roundToFixed = (value, places) => {
  return value.toFixed(places)
}

const roundDownToFixed = (value, places) => {
  return Math.floor(value).toFixed(places)
}

const getChangeLog = () => {
  return new Promise((resolve, reject) => {
    fetch('/changelog.json')
      .then((res) => res.json())
      .then((data) => {
        resolve(data)
      })
      .catch((err) => reject(err))
  })
}

const nudgeTime = () => {
  const length = 32
  const pool = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let randomId = ''
  for (var i = 0; i < length; i++) randomId += pool.charAt(Math.floor(Math.random() * pool.length))
  return `${moment().format('x')}_${randomId}`
}

const deDupeUnitList = (unitList) => {
  const deDupeList = {}

  unitList.forEach((unit) => {
    if (!unit) return

    // key = "12345678_EDF Energy"
    // or key = "12345678_owner"
    // or key = "12345678"
    const key = unit.groupName ? `${unit.unitId}_${unit.groupName}` : unit.relationship ? `${unit.unitId}_${unit.relationship}` : unit.unitId

    if (!deDupeList[key]) {
      // unit with this key isn't in dudupe list, so add it
      deDupeList[key] = { ...unit }
    } else {
      // unit with this key is already in dudupe list, so compare it's registrration date
      if (parseInt(unit.registrationDateUtc, 10) > parseInt(deDupeList[key].registrationDateUtc, 10)) {
        deDupeList[key] = { ...unit }
      }
    }
  })

  return Object.keys(deDupeList).map((key) => deDupeList[key])
}

function isScheduleSet(schedule, specificDay = false) {
  if (!schedule || typeof schedule !== 'object') return false

  if (specificDay) {
    if (typeof schedule[specificDay] !== 'object') return false
    if (!schedule[specificDay].length) return false
    return true
  }

  if (typeof schedule.monday !== 'object') return false
  if (typeof schedule.tuesday !== 'object') return false
  if (typeof schedule.wednesday !== 'object') return false
  if (typeof schedule.thursday !== 'object') return false
  if (typeof schedule.friday !== 'object') return false
  if (typeof schedule.saturday !== 'object') return false
  if (typeof schedule.sunday !== 'object') return false

  if (
    !schedule.monday.length &&
    !schedule.tuesday.length &&
    !schedule.wednesday.length &&
    !schedule.thursday.length &&
    !schedule.friday.length &&
    !schedule.saturday.length &&
    !schedule.sunday.length
  )
    return false

  return true
}

function isScheduleDayGapless(schedule, specificDay) {
  // schedule[specificDay]: [
  //   {
  //       "state": "force-charge",
  //       "start": "14:30:00",
  //       "end": "17:00:00"
  //   },
  //   {
  //       "state": "only-discharge",
  //       "start": "17:00:00",
  //       "end": "18:30:00"
  //   }
  // ]

  if (!schedule || typeof schedule !== 'object') return false
  if (typeof schedule[specificDay] !== 'object') return false
  if (!schedule[specificDay].length) return false

  function slotsCoverWholeDay(slots) {
    // Sort the slots by their start times.
    slots.sort((a, b) => a.start.localeCompare(b.start))

    // Initialize a variable to keep track of the end time of the previous slot.
    let previousEnd = '00:00:00'

    // Iterate through the sorted slots array.
    for (const slot of slots) {
      const { start, end } = slot

      // Convert start and end times to Date objects for comparison.
      const startTime = new Date(`1970-01-01T${start}`)
      const endTime = new Date(`1970-01-01T${end}`)
      const previousEndTime = new Date(`1970-01-01T${previousEnd}`)

      // Check if there is a gap between the current slot and the previous slot.
      if (startTime > previousEndTime) {
        return false // There is a gap.
      }

      // Update the previousEnd variable with the end time of the current slot.
      previousEnd = end > previousEnd ? end : previousEnd
    }

    // Check if the last slot ends at or after "23:59:59".
    return previousEnd >= '23:59:59'
  }

  return slotsCoverWholeDay(schedule[specificDay])
}

function friendlyNameFromScheduleState(state) {
  switch (state) {
    case 'only-charge':
      return getEnglishText('operating-states : only-charge')
    case 'force-charge':
      return getEnglishText('operating-states : force-charge')
    case 'only-discharge':
      return getEnglishText('operating-states : only-discharge')
    case 'force-discharge':
      return getEnglishText('operating-states : force-discharge')
    case 'normal':
      return getEnglishText('operating-states : normal')
    case 'dormant':
      return getEnglishText('operating-states : dormant')
    case 'disable':
      return getEnglishText('operating-states : disabled')
    case 'ffr-high':
      return getEnglishText('operating-states : ffr-high')
    case 'ffr-low':
      return getEnglishText('operating-states : ffr-low')
    case 'ffr-prep-high':
      return getEnglishText('operating-states : ffr-prep-high')
    case 'ffr-prep-low':
      return getEnglishText('operating-states : ffr-prep-low')
    case 'eps-enabled':
      return getEnglishText('operating-states : eps-enabled')
    case 'eps-disabled':
      return getEnglishText('operating-states : eps-disabled')
    default:
      return getEnglishText('operating-states : normal')
  }
}

function isValidEmailAddress(email) {
  const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/
  return regex.test(email)
}

function isStaffEmail(email) {
  return String(email || '').toLowerCase().includes('@powervault.co.uk')
}

function isSuperUser(user) {
  if (!user || typeof user !== 'object') return false
  if (user.isSuperUser === true) return true
  return user.user?.isSuperUser === true
}

function getTimeData() {
  // Start with UTC time to ensure consistent base
  const now = moment.utc()
  const browserTz = moment.tz.guess()
  const unitTz = 'Europe/London'

  // Convert UTC time to respective timezones
  const browserTime = now.clone().tz(browserTz)
  const unitTime = now.clone().tz(unitTz)

  const timeData = {
    browserTz,
    browserTime,

    unitTz,
    unitTime,

    unitTime_StartOfToday: unitTime.clone().startOf('day'),
    unitTime_EndOfToday: unitTime.clone().endOf('day'),

    unitTime_StartOfWeek: unitTime.clone().startOf('isoWeek'),
    unitTime_EndOfWeek: unitTime.clone().endOf('isoWeek'),

    unitTime_StartOfMonth: unitTime.clone().startOf('month'),
    unitTime_EndOfMonth: unitTime.clone().endOf('month'),

    unitTime_StartOfYear: unitTime.clone().startOf('year'),
    unitTime_EndOfYear: unitTime.clone().endOf('year'),

    unitTime_StartOfYesterday: unitTime.clone().subtract(1, 'day').startOf('day'),
    unitTime_EndOfYesterday: unitTime.clone().subtract(1, 'day').endOf('day'),

    unitTime_StartOfLastWeek: unitTime.clone().subtract(1, 'week').startOf('isoWeek'),
    unitTime_EndOfLastWeek: unitTime.clone().subtract(1, 'week').endOf('isoWeek'),

    unitTime_StartOfLastMonth: unitTime.clone().subtract(1, 'month').startOf('month'),
    unitTime_EndOfLastMonth: unitTime.clone().subtract(1, 'month').endOf('month'),

    unitTime_StartOfLastYear: unitTime.clone().subtract(1, 'year').startOf('year'),
    unitTime_EndOfLastYear: unitTime.clone().subtract(1, 'year').endOf('year'),

    differentTimezones: browserTz !== unitTz,
    differentTimezoneMessage: browserTz !== unitTz ? ` (${unitTz})` : '',

    utcTime: now,

    utcTime_StartOfToday: now.clone().startOf('day'),
    utcTime_EndOfToday: now.clone().endOf('day'),

    utcTime_StartOfWeek: now.clone().startOf('isoWeek'),
    utcTime_EndOfWeek: now.clone().endOf('isoWeek'),

    utcTime_StartOfMonth: now.clone().startOf('month'),
    utcTime_EndOfMonth: now.clone().endOf('month'),

    utcTime_StartOfYear: now.clone().startOf('year'),
    utcTime_EndOfYear: now.clone().endOf('year'),

    utcTime_StartOfYesterday: now.clone().subtract(1, 'day').startOf('day'),
    utcTime_EndOfYesterday: now.clone().subtract(1, 'day').endOf('day'),

    utcTime_StartOfLastWeek: now.clone().subtract(1, 'week').startOf('isoWeek'),
    utcTime_EndOfLastWeek: now.clone().subtract(1, 'week').endOf('isoWeek'),

    utcTime_StartOfLastMonth: now.clone().subtract(1, 'month').startOf('month'),
    utcTime_EndOfLastMonth: now.clone().subtract(1, 'month').endOf('month'),

    utcTime_StartOfLastYear: now.clone().subtract(1, 'year').startOf('year'),
    utcTime_EndOfLastYear: now.clone().subtract(1, 'year').endOf('year')
  }

  return timeData
}

/** True when dayMoment falls on the current unit-timezone (Europe/London) calendar day. */
function isUnitToday(dayMoment) {
  return getTimeData().unitTime_StartOfToday.isSame(dayMoment, 'day')
}

/**
 * Invoke callback at each Europe/London midnight, then reschedule.
 * Returns an unsubscribe function.
 */
function subscribeUnitMidnight(callback) {
  let timeoutId = null

  function scheduleNext() {
    const nextMidnightMs = getTimeData().unitTime_StartOfToday.clone().add(1, 'day').valueOf()
    const delayMs = Math.max(nextMidnightMs - Date.now(), 1000)

    timeoutId = setTimeout(() => {
      callback()
      scheduleNext()
    }, delayMs)
  }

  scheduleNext()
  return () => {
    if (timeoutId != null) clearTimeout(timeoutId)
  }
}

/** Invoke callback when this tab becomes visible again (background / sleep recovery). */
function subscribePageVisible(callback) {
  function onVisibilityChange() {
    if (document.visibilityState === 'visible') callback()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => document.removeEventListener('visibilitychange', onVisibilityChange)
}

export {
  config,
  parseModelCode,
  parseFirmwareString,
  friendlyFormat,
  isJson,
  queryString,
  roundTo,
  getChangeLog,
  nudgeTime,
  deDupeUnitList,
  isScheduleSet,
  isScheduleDayGapless,
  friendlyNameFromScheduleState,
  isValidEmailAddress,
  isStaffEmail,
  isSuperUser,
  debugMessage,
  moment,
  getPaymentGatewayUrl,
  launchAccountPayment,
  launchUnitPayment,
  cleanup,
  getTimeData,
  isUnitToday,
  subscribeUnitMidnight,
  subscribePageVisible,
  clearChartDataCache,
  isLegacyP3Unit,
  customerOwnsOnlyLegacyUnits,
  customerHasModernUnit,
  getUnitTimezone,
  getLegacyP3LastChartDayStart,
  getLegacyP3DataCutoffEnd,
  selectedUnitsIncludeLegacyP3,
  selectedUnitsHaveChartCsvExport,
  selectedUnitsHaveChartCsvFullExport,
  getChartCsvExportEarliestDay,
  CHART_CSV_FULL_EXPORT_EARLIEST_DATE,
  CHART_CSV_EXPORT_HISTORY_MAX_DAYS,
  allSelectedUnitsAreLegacyP3,
  getMaxChartDayForUnits,
  clampChartDayToLegacyLimit,
  isLegacyUnitLiveViewResponse,
  shouldApplyLegacyP3Restrictions,
  countReportingP3Units,
  formatSelectedUnitsCountSuffix
}
